import { useState, useRef } from 'react'
import { createContent, uploadFile, importUrl, listSpaces } from '../lib/api'
import {
  X,
  FileText,
  Globe,
  Upload,
  Link,
  File,
  Image as ImageIcon,
  Mic,
  BookOpen,
  FileSpreadsheet,
  Presentation,
  Loader2,
  Plus,
  FolderOpen,
  ChevronRight,
} from 'lucide-react'

const importOptions = [
  {
    id: 'note',
    icon: FileText,
    label: 'New Note',
    description: 'Create a markdown note',
    color: '#8B5CF6',
  },
  {
    id: 'url',
    icon: Globe,
    label: 'Web Page',
    description: 'Import from URL',
    color: '#06B6D4',
  },
  {
    id: 'pdf',
    icon: FileText,
    label: 'PDF',
    description: 'Upload PDF document',
    color: '#EF4444',
    accept: '.pdf',
  },
  {
    id: 'word',
    icon: FileText,
    label: 'Word',
    description: 'Upload Word document',
    color: '#2563EB',
    accept: '.doc,.docx',
  },
  {
    id: 'excel',
    icon: FileSpreadsheet,
    label: 'Excel',
    description: 'Upload spreadsheet',
    color: '#22C55E',
    accept: '.xls,.xlsx',
  },
  {
    id: 'powerpoint',
    icon: Presentation,
    label: 'PowerPoint',
    description: 'Upload presentation',
    color: '#F97316',
    accept: '.ppt,.pptx',
  },
  {
    id: 'epub',
    icon: BookOpen,
    label: 'eBook',
    description: 'Upload EPUB file',
    color: '#A855F7',
    accept: '.epub',
  },
  {
    id: 'image',
    icon: ImageIcon,
    label: 'Image',
    description: 'Upload image file',
    color: '#EC4899',
    accept: '.jpg,.jpeg,.png,.gif,.webp,.heic',
  },
  {
    id: 'audio',
    icon: Mic,
    label: 'Audio',
    description: 'Upload audio file',
    color: '#14B8A6',
    accept: '.mp3,.wav,.m4a,.aac,.ogg',
  },
  {
    id: 'text',
    icon: FileText,
    label: 'Text File',
    description: 'Upload text/markdown',
    color: '#6B7280',
    accept: '.txt,.md,.json,.csv,.xml,.html',
  },
  {
    id: 'file',
    icon: File,
    label: 'Any File',
    description: 'Upload any file type',
    color: '#78716C',
    accept: '*',
  },
]

export function ImportContentModal({ isOpen, onClose, onSuccess, defaultSpaceId }) {
  const [step, setStep] = useState('select') // 'select' | 'url' | 'note' | 'uploading'
  const [url, setUrl] = useState('')
  const [noteTitle, setNoteTitle] = useState('')
  const [noteContent, setNoteContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const fileInputRef = useRef(null)

  const resetState = () => {
    setStep('select')
    setUrl('')
    setNoteTitle('')
    setNoteContent('')
    setLoading(false)
    setError(null)
    setSelectedFile(null)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleOptionClick = (option) => {
    if (option.id === 'note') {
      setStep('note')
    } else if (option.id === 'url') {
      setStep('url')
    } else if (option.accept) {
      // Trigger file picker
      if (fileInputRef.current) {
        fileInputRef.current.accept = option.accept === '*' ? '' : option.accept
        fileInputRef.current.click()
      }
    }
  }

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFile(file)
    setStep('uploading')
    setLoading(true)
    setError(null)

    try {
      await uploadFile({
        file,
        title: file.name,
        spaceId: defaultSpaceId,
      })
      onSuccess?.()
      handleClose()
    } catch (err) {
      console.error('Upload error:', err)
      setError(err.message || 'Failed to upload file')
      setStep('select')
    } finally {
      setLoading(false)
    }
  }

  const handleImportUrl = async () => {
    if (!url.trim()) return

    // Validate URL format
    let validUrl = url.trim()
    if (!validUrl.startsWith('http://') && !validUrl.startsWith('https://')) {
      validUrl = 'https://' + validUrl
    }

    setLoading(true)
    setError(null)

    try {
      console.log('Importing URL:', validUrl, 'to space:', defaultSpaceId)
      await importUrl({
        url: validUrl,
        spaceId: defaultSpaceId,
      })
      onSuccess?.()
      handleClose()
    } catch (err) {
      console.error('Import URL error:', err)
      // Provide more helpful error message
      if (err.message?.includes('500') || err.message?.includes('Internal Server Error')) {
        setError('Failed to fetch the URL content. The website may be blocking access or the URL may be invalid.')
      } else {
        setError(err.message || 'Failed to import URL')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreateNote = async () => {
    if (!noteTitle.trim()) {
      setError('Please enter a title')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await createContent({
        title: noteTitle.trim(),
        content: noteContent,
        spaceId: defaultSpaceId,
        type: 'note',
      })
      onSuccess?.()
      handleClose()
    } catch (err) {
      console.error('Create note error:', err)
      setError(err.message || 'Failed to create note')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} />
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[550px] md:max-h-[80vh] bg-card rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step !== 'select' && (
              <button
                onClick={() => setStep('select')}
                className="p-1 rounded-lg hover:bg-accent"
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
            )}
            <h2 className="text-lg font-semibold">
              {step === 'select' && 'Add Content'}
              {step === 'url' && 'Import Web Page'}
              {step === 'note' && 'New Note'}
              {step === 'uploading' && 'Uploading...'}
            </h2>
          </div>
          <button onClick={handleClose} className="p-1 rounded-lg hover:bg-accent">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {step === 'select' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {importOptions.map((option) => {
                const Icon = option.icon
                return (
                  <button
                    key={option.id}
                    onClick={() => handleOptionClick(option)}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-accent/50 transition-all group"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${option.color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: option.color }} />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-sm">{option.label}</p>
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {step === 'url' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/article"
                  className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-primary/20 outline-none"
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted-foreground">
                We'll extract the content and make it searchable in your knowledge base.
              </p>
            </div>
          )}

          {step === 'note' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  placeholder="My new note"
                  className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-primary/20 outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Content (Markdown)</label>
                <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Start writing..."
                  rows={8}
                  className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-primary/20 outline-none resize-none font-mono text-sm"
                />
              </div>
            </div>
          )}

          {step === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
              <p className="font-medium">Uploading {selectedFile?.name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                This may take a moment...
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {(step === 'url' || step === 'note') && (
          <div className="p-4 border-t border-border flex justify-end gap-2">
            <button
              onClick={() => setStep('select')}
              className="px-4 py-2 rounded-lg hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={step === 'url' ? handleImportUrl : handleCreateNote}
              disabled={loading || (step === 'url' && !url.trim()) || (step === 'note' && !noteTitle.trim())}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {step === 'url' ? 'Import' : 'Create'}
            </button>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>
    </>
  )
}
